import { CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import type { BulkCreateMachineResponse } from '../../types/machine-setup-types';

interface BulkResultStepProps {
  result: BulkCreateMachineResponse | null;
  error: string | null;
  onDone: () => void;
}

export function BulkResultStep({ result, error, onDone }: BulkResultStepProps) {
  if (error || !result) {
    return (
      <div className="text-center py-8">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-100 mb-4">
          <XCircle className="h-8 w-8 text-red-600" />
        </div>
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">Bulk Creation Failed</h2>
        <p className="text-sm text-red-600 bg-red-50 rounded-md px-4 py-3 border border-red-200 max-w-md mx-auto mb-8">
          {error ?? 'An unexpected error occurred.'}
        </p>
        <Button variant="outline" onClick={onDone}>Done</Button>
      </div>
    );
  }

  const successCount = result.results.filter((r) => r.isSuccess).length;
  const failCount    = result.results.length - successCount;
  const allOk        = failCount === 0;

  return (
    <div className="py-4">
      <div className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full mb-4 ${allOk ? 'bg-green-100' : 'bg-amber-100'}`}>
        {allOk
          ? <CheckCircle className="h-8 w-8 text-green-600" />
          : <AlertTriangle className="h-8 w-8 text-amber-600" />
        }
      </div>
      <h2 className="text-2xl font-semibold text-gray-900 mb-1 text-center">
        {allOk ? 'All Machines Created' : `${successCount} of ${result.results.length} Created`}
      </h2>
      <p className="text-sm text-gray-500 mb-6 text-center">
        {allOk
          ? 'All machines were successfully created in Lynx.'
          : `${failCount} machine${failCount !== 1 ? 's' : ''} failed. See details below.`}
      </p>

      <div className="rounded-xl border border-gray-200 divide-y divide-gray-100 mb-6 max-h-80 overflow-y-auto">
        {result.results.map((r, i) => (
          <div key={i} className="flex items-start gap-3 px-4 py-3">
            {r.isSuccess
              ? <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
              : <XCircle    className="h-4 w-4 text-red-500  mt-0.5 shrink-0" />
            }
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">{r.machineName}</p>
              {r.isSuccess && r.machineId && (
                <p className="text-xs text-gray-400 font-mono">ID: {r.machineId}</p>
              )}
              {r.warning && (
                <p className="text-xs text-amber-600 mt-0.5">{r.warning}</p>
              )}
              {!r.isSuccess && r.error && (
                <p className="text-xs text-red-500 mt-0.5">{r.error}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="text-center">
        <Button onClick={onDone}>Done</Button>
      </div>
    </div>
  );
}
